import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import api, { getErrorMessage } from "../api/axios";
import { hasFullAccess } from "../auth/auth";
import { ACCENT, NAVY } from "../theme";
import RefreshButton from "../components/ui/RefreshButton";

const CANDIDATE_HEADERS = [
  "firstName",
  "lastName",
  "phone",
  "email",
  "candidateDate",
  "jobCode",
  "recruiterName",
  "currentRole",
  "companyName",
  "domain",
  "skills",
  "relevantSkills",
  "currentLocation",
  "expectedLocation",
  "currentSalary",
  "expectedSalary",
  "totalExperience",
  "linkedinUrl",
  "panNumber",
  "comments",
];

const JOB_HEADERS = [
  "jobCode",
  "companyName",
  "domain",
  "roleName",
  "skills",
  "relevantSkills",
  "mandatorySkills",
  "salary",
  "experience",
  "relevantExperience",
  "location",
  "spocName",
  "positionDate",
  "jobDescription",
];

// Columns that hold dates and must be saved EXACTLY as uploaded (no shifting).
const DATE_FIELDS = ["positionDate", "candidateDate"];

// Colors: navy/accent now reference the shared design tokens from theme.js
// (Phase 1) instead of separately-hardcoded hex values, so this page stays
// visually in sync with the rest of the redesigned app. `border` is tightened
// to match the border color used on every other page's cards
// (theme.js's MuiCard override). Every other value here (green/red/orange/
// lightBlue/lightGreen/lightRed/gray) is a presentational summary-tile color
// specific to this page, not a formal shared token, so those are left exactly
// as they were.
const COLORS = {
  navy: NAVY, // was "#1a2744" — now the same navy used everywhere else
  accent: ACCENT, // was already the same hex ("#2563eb"), now single-sourced
  green: "#16a34a",
  red: "#dc2626",
  orange: "#ea580c",
  lightBlue: "#eff6ff",
  lightGreen: "#f0fdf4",
  lightRed: "#fef2f2",
  border: "#e6eaf0", // was "#e2e8f0" — now matches the shared card border
  gray: "#64748b",
};

// Matches the box-shadow every other redesigned page's cards use
// (theme.js's MuiCard override), applied here since this page's cards are
// plain styled divs rather than MUI <Card>/<Paper> components.
const CARD_SHADOW = "0 1px 3px rgba(15,27,51,0.05)";

const pad = (n) => String(n).padStart(2, "0");

/**
 * Convert any date a user might put in Excel into a clean "YYYY-MM-DD" string,
 * WITHOUT any timezone conversion — so the exact date they typed is the exact
 * date that gets saved.
 *
 * Handles:
 *  - "2026-06-11"            (ISO text — kept verbatim)
 *  - "2026/06/11"           (year-first with slashes)
 *  - "11/06/2026"           (day-first — Indian style, assumed for ambiguous)
 *  - a real Date object     (Excel date cell read with cellDates:true)
 *  - an Excel serial number (fallback, converted timezone-free)
 *  - blank / junk           -> "" (row won't crash the backend)
 */
function toIsoDate(val) {
  if (val === null || val === undefined || val === "") return "";

  // ---- String values: parse the digits directly, never via new Date() ----
  if (typeof val === "string") {
    const s = val.trim();
    if (!s) return "";

    // Already ISO (YYYY-MM-DD...) -> take the first 10 chars exactly
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    const parts = s.split(/[/\-.]/).map((p) => p.trim());
    if (parts.length === 3) {
      const [a, b, c] = parts;
      // year-first: 2026/06/11
      if (a.length === 4) return `${a}-${pad(b)}-${pad(c)}`;
      // year-last: dd/mm/yyyy or mm/dd/yyyy
      if (c.length === 4) {
        let day, month;
        if (parseInt(a, 10) > 12) {
          // first number can't be a month -> it's the day (day-first)
          day = a;
          month = b;
        } else {
          // ambiguous -> assume DAY-first (Indian dd/mm/yyyy)
          day = a;
          month = b;
        }
        return `${c}-${pad(month)}-${pad(day)}`;
      }
    }
    return ""; // unrecognised text -> blank, fail-safe
  }

  // ---- Real Date object (from cellDates:true) -> use LOCAL components ----
  if (val instanceof Date && !isNaN(val.getTime())) {
    return `${val.getFullYear()}-${pad(val.getMonth() + 1)}-${pad(
      val.getDate(),
    )}`;
  }

  // ---- Excel serial number -> convert with SheetJS (timezone-free) ----
  if (typeof val === "number" && XLSX.SSF && XLSX.SSF.parse_date_code) {
    const d = XLSX.SSF.parse_date_code(val);
    if (d && d.y) return `${d.y}-${pad(d.m)}-${pad(d.d)}`;
  }

  return "";
}

// Normalise every date column in every row before preview/upload.
function normalizeRows(rows) {
  return rows.map((r) => {
    const out = { ...r };
    DATE_FIELDS.forEach((f) => {
      if (f in out) out[f] = toIsoDate(out[f]);
    });
    return out;
  });
}

// ── Phase 15 (Design Gap Audit item O) ──────────────────────────────────────
// Real client-side pre-upload validation, matching the prototype's actual
// stated gap ("a bad row fails silently after upload — here you see and fix
// the bad rows first"). Every check below is a real, existing business rule
// (required fields the backend already needs, a standard email format, and
// duplicate detection against already-loaded /candidates data) — nothing
// fabricated. This does NOT change the /candidates/bulk or /jobs/bulk
// endpoints; it only decides, client-side, which parsed rows are safe to
// send and lets the user see problems before clicking Upload instead of
// only finding out after.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateRow(row, type, existingCandidates) {
  if (type === "candidates") {
    if (!row.jobCode) return { status: "error", reason: "Job code missing" };
    if (row.email && !EMAIL_RE.test(String(row.email))) {
      return { status: "error", reason: "Invalid email" };
    }
    if (!row.phone) return { status: "error", reason: "Phone required" };
    const isDup = existingCandidates.some(
      (c) =>
        (row.email &&
          c.email &&
          c.email.toLowerCase() === String(row.email).toLowerCase()) ||
        (row.phone && c.phone && c.phone === String(row.phone)),
    );
    if (isDup)
      return { status: "duplicate", reason: "Duplicate of existing profile" };
    return { status: "ok", reason: "Ready" };
  }
  // type === "jobs"
  if (!row.jobCode) return { status: "error", reason: "Job code missing" };
  if (!row.companyName) return { status: "error", reason: "Company missing" };
  return { status: "ok", reason: "Ready" };
}

export default function BulkUpload() {
  const [type, setType] = useState("candidates");
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const fullAccess = hasFullAccess();

  // ── Phase 15 additions ──
  const [existingCandidates, setExistingCandidates] = useState([]);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  useEffect(() => {
    // Loaded once for real duplicate detection (existing endpoint, reused —
    // not a new API). Non-critical: if it fails, validation still runs,
    // just without duplicate detection for this session.
    api
      .get("/candidates")
      .then((r) => setExistingCandidates(r.data || []))
      .catch(() => {});
  }, []);

  const headers = type === "candidates" ? CANDIDATE_HEADERS : JOB_HEADERS;
  const endpoint = type === "candidates" ? "/candidates/bulk" : "/jobs/bulk";

  // Real per-row validation results — recomputed whenever the parsed rows,
  // type, or the loaded existing-candidates list change.
  const validatedRows = rows.map((row) => ({
    row,
    ...validateRow(row, type, existingCandidates),
  }));
  const readyCount = validatedRows.filter((v) => v.status === "ok").length;
  const errorCount = validatedRows.filter((v) => v.status === "error").length;
  const duplicateCount = validatedRows.filter(
    (v) => v.status === "duplicate",
  ).length;
  const rowsToDisplay = showErrorsOnly
    ? validatedRows.filter((v) => v.status !== "ok")
    : validatedRows;
  // What will actually be sent: errors are never sendable (missing required
  // fields the backend needs); duplicates are sent unless "Skip duplicates"
  // is checked.
  const rowsToUpload = validatedRows
    .filter(
      (v) => v.status === "ok" || (v.status === "duplicate" && !skipDuplicates),
    )
    .map((v) => v.row);

  useEffect(() => {
    if (fullAccess) fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/bulk/history");
      setHistory(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      // cellDates: true -> date cells become real Date objects (not serials)
      const wb = XLSX.read(evt.target.result, {
        type: "binary",
        cellDates: true,
      });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
      // Fix all date columns so the EXACT uploaded date is what gets saved
      setRows(normalizeRows(data));
    };
    reader.readAsBinaryString(file);
  };

  const handleUpload = async () => {
    if (!rowsToUpload.length) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post(endpoint, rowsToUpload);
      setResult(res.data);
      setRows([]);
      setFileName("");
      if (fullAccess) fetchHistory();
    } catch (err) {
      setResult({
        total: rowsToUpload.length,
        success: 0,
        failed: rowsToUpload.length,
        errors: [{ row: "-", reason: getErrorMessage(err, "Upload failed") }],
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    // Set column widths
    ws["!cols"] = headers.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `${type}_bulk_template.xlsx`);
  };

  const downloadErrorReport = () => {
    const problemRows = validatedRows.filter((v) => v.status !== "ok");
    if (!problemRows.length) return;
    const data = problemRows.map((v) => ({
      ...v.row,
      validationIssue: v.reason,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Issues");
    XLSX.writeFile(wb, `${type}_validation_issues.xlsx`);
  };

  const switchType = (t) => {
    // Recruiters can only use the candidates tab
    if (t === "jobs" && !fullAccess) return;
    setType(t);
    setRows([]);
    setFileName("");
    setResult(null);
  };

  return (
    <div style={{ padding: "16px 20px", maxWidth: 960, margin: "0 auto" }}>
      {/* Scoped hover-only styles — inline style objects can't express :hover,
          so this small stylesheet adds purely visual hover feedback to cards,
          buttons, the upload dropzone, and table rows. No component logic,
          state, or markup structure is affected by this block. */}
      <style>{`
        .bu-card { transition: box-shadow 0.2s ease; }
        .bu-card:hover { box-shadow: 0 4px 12px rgba(15,27,51,0.08); }
        .bu-btn-outline { transition: background 0.15s ease, box-shadow 0.15s ease; }
        .bu-btn-outline:hover { background: ${COLORS.lightBlue}; }
        .bu-btn-solid { transition: box-shadow 0.15s ease, opacity 0.15s ease; }
        .bu-btn-solid:hover:not(:disabled) { box-shadow: 0 4px 12px rgba(37,99,235,0.30); }
        .bu-toggle-inactive { transition: background 0.15s ease, color 0.15s ease; }
        .bu-toggle-inactive:hover { background: #dbe2ea; }
        .bu-dropzone { transition: background 0.15s ease; }
        .bu-dropzone:hover { background: #e0ecff; }
        .bu-table tbody tr:hover { background: #f6f8fb; }
        .bu-refresh-btn { transition: background 0.15s ease; }
        .bu-refresh-btn:hover { background: #f8fafc; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <p style={{ margin: 0, color: COLORS.gray, fontSize: 14 }}>
          Upload multiple candidates or positions at once using Excel or CSV
        </p>
      </div>

      {/* Type toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {[
          { key: "candidates", label: "👤 Candidates" },
          // Only ADMIN / MANAGER can bulk-upload positions. Recruiters: candidates only.
          ...(fullAccess ? [{ key: "jobs", label: "💼 Positions" }] : []),
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => switchType(key)}
            className={type === key ? undefined : "bu-toggle-inactive"}
            style={{
              padding: "8px 20px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              background: type === key ? COLORS.accent : COLORS.border,
              color: type === key ? "#fff" : COLORS.gray,
              transition: "all 0.2s",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Upload card */}
      <div
        className="bu-card"
        style={{
          background: "#fff",
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          boxShadow: CARD_SHADOW,
          padding: 24,
          marginBottom: 24,
        }}
      >
        {/* Step 1 - Download template */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 600, color: COLORS.navy }}>
            Step 1 — Download Template
          </p>
          <button
            onClick={downloadTemplate}
            className="bu-btn-outline"
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              cursor: "pointer",
              background: "#fff",
              border: `1.5px solid ${COLORS.accent}`,
              color: COLORS.accent,
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            ⬇ Download Excel Template
          </button>
          <p style={{ margin: "6px 0 0", fontSize: 12, color: COLORS.gray }}>
            Fill in the template and upload it below.
            {type === "jobs"
              ? " For positionDate, use the format YYYY-MM-DD (e.g. 2026-06-11) — that exact date will be saved."
              : ""}
          </p>
        </div>

        {/* Step 2 - Upload file */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 600, color: COLORS.navy }}>
            Step 2 — Upload File
          </p>
          <label
            className="bu-dropzone"
            style={{
              display: "inline-block",
              padding: "8px 18px",
              borderRadius: 8,
              border: `1.5px dashed ${COLORS.accent}`,
              cursor: "pointer",
              color: COLORS.accent,
              fontWeight: 600,
              fontSize: 13,
              background: COLORS.lightBlue,
            }}
          >
            📂 Choose Excel / CSV file
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFile}
              style={{ display: "none" }}
            />
          </label>
          {fileName && (
            <span style={{ marginLeft: 12, fontSize: 13, color: COLORS.gray }}>
              📄 {fileName} — <strong>{rows.length}</strong> rows loaded
            </span>
          )}
        </div>

        {/* Preview table — now a real validation preview (Phase 15) */}
        {rows.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <p style={{ margin: 0, fontWeight: 600, color: COLORS.navy }}>
                Validation preview
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <span style={chipStyle(COLORS.lightGreen, COLORS.green)}>
                  {readyCount} ready
                </span>
                {errorCount > 0 && (
                  <span style={chipStyle(COLORS.lightRed, COLORS.red)}>
                    {errorCount} errors
                  </span>
                )}
                {duplicateCount > 0 && (
                  <span style={chipStyle("#fffbeb", COLORS.orange)}>
                    {duplicateCount} duplicates
                  </span>
                )}
                {(errorCount > 0 || duplicateCount > 0) && (
                  <label
                    style={{
                      fontSize: 12,
                      color: COLORS.gray,
                      display: "flex",
                      gap: 4,
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={showErrorsOnly}
                      onChange={(e) => setShowErrorsOnly(e.target.checked)}
                    />
                    Show errors only
                  </label>
                )}
                {(errorCount > 0 || duplicateCount > 0) && (
                  <button
                    onClick={downloadErrorReport}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: `1px solid ${COLORS.border}`,
                      background: "#fff",
                      cursor: "pointer",
                      fontSize: 12,
                      color: COLORS.gray,
                    }}
                  >
                    Download error report
                  </button>
                )}
              </div>
            </div>
            <div
              style={{
                overflowX: "auto",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8,
                maxHeight: 260,
              }}
            >
              <table
                className="bu-table"
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      position: "sticky",
                      top: 0,
                    }}
                  >
                    <th style={thStyle}>#</th>
                    {Object.keys(rows[0]).map((h) => (
                      <th key={h} style={thStyle}>
                        {h}
                      </th>
                    ))}
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsToDisplay.slice(0, 30).map((v, i) => (
                    <tr
                      key={i}
                      style={{
                        background:
                          v.status === "error"
                            ? "#fffafa"
                            : v.status === "duplicate"
                              ? "#fffdf5"
                              : i % 2 === 0
                                ? "#fff"
                                : "#f8fafc",
                      }}
                    >
                      <td style={tdStyle}>{rows.indexOf(v.row) + 2}</td>
                      {Object.values(v.row).map((val, j) => (
                        <td key={j} style={tdStyle}>
                          {String(val)}
                        </td>
                      ))}
                      <td style={tdStyle}>
                        <span
                          style={chipStyle(
                            v.status === "error"
                              ? COLORS.lightRed
                              : v.status === "duplicate"
                                ? "#fffbeb"
                                : COLORS.lightGreen,
                            v.status === "error"
                              ? COLORS.red
                              : v.status === "duplicate"
                                ? COLORS.orange
                                : COLORS.green,
                          )}
                        >
                          {v.reason}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rowsToDisplay.length > 30 && (
              <p
                style={{ margin: "4px 0 0", fontSize: 12, color: COLORS.gray }}
              >
                ...and {rowsToDisplay.length - 30} more rows
              </p>
            )}
          </div>
        )}

        {/* Step 3 - Upload button */}
        <div>
          <p style={{ margin: "0 0 8px", fontWeight: 600, color: COLORS.navy }}>
            Step 3 — Upload to Database
          </p>
          {duplicateCount > 0 && (
            <label
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                fontSize: 13,
                color: COLORS.gray,
                marginBottom: 10,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={skipDuplicates}
                onChange={(e) => setSkipDuplicates(e.target.checked)}
              />
              Skip duplicates ({duplicateCount} row
              {duplicateCount !== 1 ? "s" : ""} flagged)
            </label>
          )}
          <button
            onClick={handleUpload}
            disabled={!rowsToUpload.length || loading}
            className="bu-btn-solid"
            style={{
              padding: "10px 28px",
              borderRadius: 8,
              border: "none",
              background:
                rowsToUpload.length && !loading ? COLORS.accent : "#cbd5e1",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              cursor:
                rowsToUpload.length && !loading ? "pointer" : "not-allowed",
            }}
          >
            {loading
              ? "Uploading..."
              : `Import ${rowsToUpload.length} row${rowsToUpload.length !== 1 ? "s" : ""}`}
          </button>
          {errorCount > 0 && (
            <p style={{ margin: "8px 0 0", fontSize: 12, color: COLORS.red }}>
              {errorCount} row{errorCount !== 1 ? "s" : ""} with errors will not
              be uploaded — fix them in your file and re-upload, or download the
              error report above.
            </p>
          )}
        </div>
      </div>

      {/* Result card */}
      {result && (
        <div
          className="bu-card"
          style={{
            background: "#fff",
            borderRadius: 12,
            border: `1px solid ${result.failed === 0 ? "#bbf7d0" : "#fecaca"}`,
            boxShadow: CARD_SHADOW,
            padding: 24,
            marginBottom: 24,
          }}
        >
          <h3 style={{ margin: "0 0 16px", color: COLORS.navy }}>
            Upload Result
          </h3>

          {/* Summary */}
          <div
            style={{
              display: "flex",
              gap: 16,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            {[
              {
                label: "Total",
                value: result.total,
                bg: COLORS.lightBlue,
                color: COLORS.accent,
              },
              {
                label: "Success",
                value: result.success,
                bg: COLORS.lightGreen,
                color: COLORS.green,
              },
              {
                label: "Failed",
                value: result.failed,
                bg: COLORS.lightRed,
                color: COLORS.red,
              },
            ].map(({ label, value, bg, color }) => (
              <div
                key={label}
                style={{
                  padding: "12px 20px",
                  borderRadius: 8,
                  background: bg,
                  border: `1px solid ${color}22`,
                  textAlign: "center",
                  minWidth: 80,
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, color }}>
                  {value}
                </div>
                <div style={{ fontSize: 12, color: COLORS.gray }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Error table */}
          {result.errors?.length > 0 && (
            <>
              <p
                style={{
                  margin: "0 0 8px",
                  fontWeight: 600,
                  color: COLORS.red,
                }}
              >
                ❌ Failed Rows
              </p>
              <div
                style={{
                  overflowX: "auto",
                  border: `1px solid #fecaca`,
                  borderRadius: 8,
                  maxHeight: 200,
                }}
              >
                <table
                  className="bu-table"
                  style={{
                    borderCollapse: "collapse",
                    width: "100%",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr style={{ background: COLORS.lightRed }}>
                      <th style={thStyle}>Row</th>
                      <th style={thStyle}>Data</th>
                      <th style={thStyle}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>{e.row}</td>
                        <td style={tdStyle}>{e.data || "-"}</td>
                        <td style={{ ...tdStyle, color: COLORS.red }}>
                          {e.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* History */}
      {fullAccess && (
        <div
          className="bu-card"
          style={{
            background: "#fff",
            borderRadius: 12,
            border: `1px solid ${COLORS.border}`,
            boxShadow: CARD_SHADOW,
            padding: 24,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <h3 style={{ margin: 0, color: COLORS.navy }}>Upload History</h3>
            <RefreshButton onClick={fetchHistory} />
          </div>

          {historyLoading ? (
            <p style={{ color: COLORS.gray }}>Loading...</p>
          ) : history.length === 0 ? (
            <p style={{ color: COLORS.gray }}>No uploads yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                className="bu-table"
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {[
                      "Date",
                      "Type",
                      "Uploaded By",
                      "Total",
                      "Success",
                      "Failed",
                    ].map((h) => (
                      <th key={h} style={thStyle}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr
                      key={h.id}
                      style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}
                    >
                      <td style={tdStyle}>
                        {new Date(h.uploadedAt + "Z").toLocaleString()}
                      </td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            background:
                              h.uploadType === "CANDIDATE"
                                ? COLORS.lightBlue
                                : "#fef9c3",
                            color:
                              h.uploadType === "CANDIDATE"
                                ? COLORS.accent
                                : "#854d0e",
                          }}
                        >
                          {h.uploadType}
                        </span>
                      </td>
                      <td style={tdStyle}>{h.uploadedBy}</td>
                      <td style={tdStyle}>{h.totalRows}</td>
                      <td
                        style={{
                          ...tdStyle,
                          color: COLORS.green,
                          fontWeight: 600,
                        }}
                      >
                        {h.successCount}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          color: h.failedCount > 0 ? COLORS.red : COLORS.gray,
                          fontWeight: 600,
                        }}
                      >
                        {h.failedCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Flat, bottom-bordered rows with a muted uppercase header — matches the
// table style used on every other redesigned page (Positions/Candidates/
// Clients), instead of this file's original full-grid cell borders.
const thStyle = {
  padding: "8px 12px",
  borderBottom: `2px solid ${COLORS.border}`,
  textAlign: "left",
  fontWeight: 700,
  whiteSpace: "nowrap",
  fontSize: 11.5,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: COLORS.gray,
};

const tdStyle = {
  padding: "7px 12px",
  borderBottom: `1px solid ${COLORS.border}`,
  whiteSpace: "nowrap",
  fontSize: 13,
};

// Small status-chip style helper for the validation preview (Phase 15).
const chipStyle = (bg, color) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 700,
  background: bg,
  color,
  whiteSpace: "nowrap",
});
